import { Product } from '../../features/cart/shop-cart/interfaces/shop-cart.interface';
import { freeTrialTimestampsToDates } from '../../features/free-trial/utils/free-trial.util';
import { SHARPEN_ANALYTICS_PRODUCT_TYPE } from '../../features/upsell/constants/sharpen-page-type.constants';
import {
  isBoardsAndBeyondsProgram,
  isClinicalReasoningProgram,
  isFirstAidForwardProgram,
} from '../constants/professional-redirects';
import { OrderHistory } from '../../features/order-history/order-history.interface';
import { appendDuration, appendDurationForTunnelPurchaseFlow, appendedDurationForHEProducts, getTunnelProductTitleVerbose, isProfessional, isSharpenPlus } from './product-util';
import { CONNECT_LOOSELEAF_UPSELL, getProductTitleForPdpLevelAnalytics, HE, HE_LOWER, isHeStorePurchaseFlow, isTunnelPurchaseFlow, PROFESSIONAL, SEG, SEG_LOWER } from './util';
import { getUserDataForUserAnalytics } from '../../features/user-profile/user-profile.selectors';
import {
  getOrderBusinessCode,
  getOrderTypeTunnelFlow,
  getPromoCodeState,
  isProfessionalHasMixedCart,
} from '../../features/cart/shop-cart/store/shop-cart.selectors';
import { AddToCartAnalyticsData, CoverImageAnalyticsConstants, SearchTermAnalyticsConstants } from '../constants/anayltics.constants';
import {
  getCartPageLocalStorageObject,
  getPdpPageLocalStorageObject,
  getSearchPageLocalStorageObject,
} from '../../features/search/utils/search.util';
import { mhecommLocalStorage } from '../local-storage';
import { checkIsMhGoItemFromItemProperties, isHeCustomStoreOrder, isHePrivateStoreOrder } from './order-util';
import { OrderType } from '../constants/order-types';
import { OfferTypeChecker } from './offer-type-util';
import { fetchAleksProductTitleVerbose, isAleksSharpenCompanionBundle } from './aleks-sharpen-bundle-util';
import { getHeStoreDataForAnalytics } from '../../features/create-custom/utils/create-custom-product.util';
import { getCartAnalyticsOverrides } from '../../features/cart/shop-cart/shop-cart.analytics';
import { getOrderProgramIds } from '../../features/order-history/utils/order-history.util';
import {
  getSharpenPromoAnalytics,
  getSharpenPromoAnalyticsValues,
  hasSharpenPromoCode,
} from '../../features/upsell/sharpen-upsell-promo.analytics';

export enum StudentInstructorToggleGroup {
  STUDENT = 'student',
  INSTRUCTOR = 'instructor',
}

const PROFESSIONAL_PRODUCT_TYPE_ANALYTICS_LABELS = {
  BOARDS_AND_BEYONDS: 'BnB',
  FIRST_AID_FOWARD: 'FA4',
  CLINICAL_REASONING: 'CR'
};
export const PRODUCT_TYPE_FLASHCARD = 'flashcard';
export const PROFESSIONAL_FCD_TITLE_TYPE = 'FCD';

export enum AnalyticsOrderFlowTypes {
  HE_CONNECT_SHARPEN = 'he-connect-sharpenupsell',
  HE_CONNECT_SHARPEN_PROMO = 'he-connect-sharpenupsell-promo',
}

export const PhysicalCardDescriptionList: string[] = [
  'Hardcopy',
  'Print',
  'Loose Leaf',
  'Textbook Rental',
  'Connect + Textbook Rental'
]

export function getProductTypeBU({ programIds }: { programIds: string[] }): Record<string, any> {
  const productTypeBUList: string[] = [];
  programIds.forEach((programId) => {
    if (
      isBoardsAndBeyondsProgram(programId) &&
      !productTypeBUList.includes(PROFESSIONAL_PRODUCT_TYPE_ANALYTICS_LABELS.BOARDS_AND_BEYONDS)
    ) {
      productTypeBUList.push(PROFESSIONAL_PRODUCT_TYPE_ANALYTICS_LABELS.BOARDS_AND_BEYONDS);
    } else if (
      isFirstAidForwardProgram(programId) &&
      !productTypeBUList.includes(PROFESSIONAL_PRODUCT_TYPE_ANALYTICS_LABELS.FIRST_AID_FOWARD)
    ) {
      productTypeBUList.push(PROFESSIONAL_PRODUCT_TYPE_ANALYTICS_LABELS.FIRST_AID_FOWARD);
    } else if (
      isClinicalReasoningProgram(programId) &&
      !productTypeBUList.includes(PROFESSIONAL_PRODUCT_TYPE_ANALYTICS_LABELS.CLINICAL_REASONING)
    ) {
      productTypeBUList.push(PROFESSIONAL_PRODUCT_TYPE_ANALYTICS_LABELS.CLINICAL_REASONING);
    }
  });
  return {
    ...(productTypeBUList.length && { productTypeBu: productTypeBUList.join(' + ') }),
  };
}

export function getAdditionalOrderAnalytics({
  products,
  programIds,
}: {
  products?: Product[];
  programIds?: string[];
}): Record<string, any> {
  if (!(products || programIds)) {
    return {};
  }
  const progIds: string[] = programIds
    ? programIds
    : products!.map((product) => product.productIdentifier.programSourceId ?? '');
  return {
    ...getProductTypeBU({ programIds: progIds }),
  };
}

export function getSecondLevelBU({ programId }: { programId: string }): Record<string, any> {
  const details: Record<string, any> = {};
  if (isBoardsAndBeyondsProgram(programId)) {
    details.businessUnitlevel2 = PROFESSIONAL_PRODUCT_TYPE_ANALYTICS_LABELS.BOARDS_AND_BEYONDS;
  } else if (isFirstAidForwardProgram(programId)) {
    details.businessUnitlevel2 = PROFESSIONAL_PRODUCT_TYPE_ANALYTICS_LABELS.FIRST_AID_FOWARD;
  } else if (isClinicalReasoningProgram(programId)) {
    details.businessUnitlevel2 = PROFESSIONAL_PRODUCT_TYPE_ANALYTICS_LABELS.CLINICAL_REASONING;
  }
  return details;
}

export function getProductTypeInfo({ programId }: { programId: string }): Record<string, any> {
  const details: Record<string, any> = {};
  if (isSharpenPlus(programId)) {
    details.productType = SHARPEN_ANALYTICS_PRODUCT_TYPE;
  }
  return details;
}

export function getProductTitle({ product, captureProductTitle }: { product: Product; captureProductTitle: boolean }): Record<string, any> {
  const details: Record<string, any> = {};
  
  const title = isAleksSharpenCompanionBundle(product.productMetadata?.offerType, product.productIdentifier?.isSharpenCompanion)
    ? fetchAleksProductTitleVerbose(product.productMetadata?.metaOfferContentsMetadata)
    : titleWithDurationAppended(product);

  if (captureProductTitle) {
    details.productTitle = title;
  } else {
    details.title = title;
  }
  
  return details;
}

const titleWithDurationAppended = (product: Product) => {
  return (
    (isTunnelPurchaseFlow()
     ? getTunnelProductTitleVerbose(
        getProductTitleForPdpLevelAnalytics(
          product.productMetadata?.title,
          product.productMetadata?.edition,
          product.productMetadata?.isEvergreen,
          isSharpenPlus(product.productIdentifier?.programSourceId)
        ),
        product.productIdentifier
      )
     : getProductTitleForPdpLevelAnalytics(
        product.productMetadata?.title,
        product.productMetadata?.edition,
        product.productMetadata?.isEvergreen,
        isSharpenPlus(product.productIdentifier?.programSourceId)
      )) + ' '
    + (isTunnelPurchaseFlow()
      ? appendDurationForTunnelPurchaseFlow({
          duration: product.productMetadata.duration,
          durationPeriod: product.productMetadata.durationPeriod,
          term: product.productIdentifier.term,
        })
      : appendedDurationForHEProducts({
          businessUnit: product.productIdentifier.businessUnit,
          isVirtual: product.productMetadata.virtual,
          formatTypeDesc: product.productMetadata?.formatTypeDesc!,
          duration: product.productMetadata?.duration!,
          durationPeriod: product.productMetadata?.durationPeriod!,
          ...(product.productMetadata?.offerType && {offerType: product.productMetadata?.offerType}),
        })
      )
    )?.trim();
}

export function getTitleWithDuration({ 
  title,
  duration,
  durationPeriod,
  isHeDigitalProduct 
}: { 
  title: string; 
  duration: number;
  durationPeriod: string;
  isHeDigitalProduct?: boolean;
}): Record<string, any> {
  const details: Record<string, any> = {};
  details.title = (isHeDigitalProduct
    ? `${title} ` + appendDuration(duration, durationPeriod, false)
    : title)?.trim();
  
  return details;
}

export function getPdpTypeInfo({ offerType, pdpType }: { offerType?: string; pdpType?: string }): Record<string, any> {
  const details: Record<string, any> = {};
  details.pdpType = pdpType ?? 'Standard';
  if (OfferTypeChecker.isConnectLLDiscounted(offerType)) {
    details.productType = undefined;
    details.pdpType = CONNECT_LOOSELEAF_UPSELL;
  }
  return details;
}

export function getSearchTermObjectFromCartLocalStorage({ programId, showRemoveLink }: { programId: string; showRemoveLink: boolean }): Record<string, any> {
  const details: Record<string, any> = {};
  const productCartLocalStorageValue = getCartPageAnalytics(programId);
  if (productCartLocalStorageValue && !showRemoveLink) {
    details.searchText = productCartLocalStorageValue;
  } else {
    details.searchText = undefined;
  }
  return details;
}

export function getAdditionalSearchTermAnalytics({ captureProceedToCheckoutAnalytics }: { captureProceedToCheckoutAnalytics: boolean }): Record<string, any> {
  const details: Record<string, any> = {};
  if (captureProceedToCheckoutAnalytics) {
    details.searchText = getCartPageLocalStorageObject();
  }
  return details;
}

export function getSharpenAnalyticsOrderDetails(order: OrderHistory) {
  const details: Record<string, any> = {};

  order.userOrderDetails?.forEach((product) => {
    if (isSharpenPlus(product.programSourceId)) {
      details.productType = SHARPEN_ANALYTICS_PRODUCT_TYPE;
      details.sharpenDescription = product.productDescription;

      if (hasSharpenPromoCode(order.couponCode)) {
        Object.assign(details, getSharpenPromoAnalyticsValues());
      }
    }
  });

  return details;
}

export function getPdpLevelProductTitle({ addToCartAnalyticsData }: { addToCartAnalyticsData: AddToCartAnalyticsData }): Record<string, any> {
  const details: Record<string, any> = {};
  const baseTitle = getBaseTitle(addToCartAnalyticsData);

  if (!baseTitle) {
    return details;
  }
  const shouldAppendDuration = addToCartAnalyticsData.purchaseOption?.title || addToCartAnalyticsData.removeFromCartPreview;
  details.title = (shouldAppendDuration
    ? baseTitle + ' ' + appendDurationToProductTitle(addToCartAnalyticsData)
    : baseTitle)?.trim();

  return details;
}

export function getBaseTitle(data: AddToCartAnalyticsData) {
  if (data.purchaseOption?.title) {
    return getProductTitleForPdpLevelAnalytics(
      data.purchaseOption?.title,
      data?.edition,
      data?.isEvergreenProduct,
      isSharpenPlus(data?.programSourceId)
    );
  } else if (data?.productTitle) {
    return data.removeFromCartPreview || !window.location.href.includes('/highered/') || window.location.href.includes('/digital-products/compass/')
      ? getProductTitleForPdpLevelAnalytics(
          data?.productTitle,
          data?.edition,
          data?.isEvergreenProduct,
          isSharpenPlus(data?.programSourceId)
        )
      : data?.productTitle;
  }
  return undefined;
}

export function getCoverImage(product: Product): string {
  return (product?.productIdentifier?.businessUnit === HE && OfferTypeChecker.isHeEbook(product?.productMetadata?.offerType)) ? CoverImageAnalyticsConstants.NOT_SHOWN : CoverImageAnalyticsConstants.SHOWN;
}

function appendDurationToProductTitle(data: AddToCartAnalyticsData) {
  return appendedDurationForHEProducts({
    businessUnit: determineBusinessUnit(data.businessUnit?.toUpperCase()!),
    isVirtual: data.isVirtual ? true : false,
    formatTypeDesc: data.poType!,
    duration: data.duration ?? data.purchaseOption?.duration!,
    durationPeriod: data.durationPeriod ?? data.purchaseOption?.durationPeriod!,
    ...((data?.offerType || data?.poType) && {offerType: data?.offerType ?? data?.poType}),
  });
}

export function getProductsWithAdditionalAnalytics({ products }: { products: Product[] }): Record<string, any>[] {
  return products.map((product) => {
    const { titleType, division, ...productMetadata } = product.productMetadata;
    const isProfessionalProduct = isProfessional(product.productIdentifier.businessUnit ?? '');

    return {
      ...product,
      freeTrialInfo: freeTrialTimestampsToDates(product.freeTrialInfo),
      productIdentifier: {
        ...product.productIdentifier,
        ...getSecondLevelBU({ programId: product.productIdentifier.programSourceId ?? '' }),
        ...(titleType === PROFESSIONAL_FCD_TITLE_TYPE && {
          productType: PRODUCT_TYPE_FLASHCARD,
        }),
      },
      productMetadata: {
        ...productMetadata,
        ...getProductTitle({
          product: product,
          captureProductTitle: false,
        }),
        ...getProductTypeInfo({
          programId: product.productIdentifier.programSourceId ?? '',
        }),
        ...getSharpenTunnelFlowProperties({
          isSharpenCompanion: product.productIdentifier.isSharpenCompanion,
          isSharpenAdvantage: product.productIdentifier.isSharpenAdvantage,
          isConnectSharpenBundle: product.productIdentifier.isConnectSharpenBundle,
          itemType: product.productMetadata.itemType,
          offerType: product.productMetadata.offerType,
          formatCode: product.productMetadata.formatCode,
          flow: product.productMetadata.flow,
        }),
        ...(isProfessionalProduct && titleType !== undefined && { titleType }),
        ...(isProfessionalProduct && division !== undefined && { division }),
        ...(titleType === PROFESSIONAL_FCD_TITLE_TYPE && {
          productType: PRODUCT_TYPE_FLASHCARD,
        }),
      },
      coverImage: getCoverImage(product)
    };
  });
}

export function captureAddToCartAnalytics(
  addToCartAnalyticsData: AddToCartAnalyticsData,
) {
  const isProfessionalProduct = isProfessional(addToCartAnalyticsData.businessUnit ?? '');
  const { titleType, division } = addToCartAnalyticsData.product ?? {};
  const resolvedProductType =
    titleType === PROFESSIONAL_FCD_TITLE_TYPE
      ? PRODUCT_TYPE_FLASHCARD
      : (addToCartAnalyticsData.productType ?? addToCartAnalyticsData.format);
  const details: any = {
    isbn: addToCartAnalyticsData.isbn,
    ...(addToCartAnalyticsData.isWishListProduct && {
      wishListId: addToCartAnalyticsData.wishList?.wishlist_id!,
      wishListName: addToCartAnalyticsData.wishList?.name!,
      pageType: addToCartAnalyticsData.addToCartPageType!,
    }),
    businessUnit: determineBusinessUnit(addToCartAnalyticsData.businessUnit?.toUpperCase()!),
    copyrightYear: addToCartAnalyticsData.copyrightYear,
    catalog: addToCartAnalyticsData.catalog,
    instructorOnly: addToCartAnalyticsData.instructorOnly,
    isbn13: addToCartAnalyticsData.isbn13,
    netPrice: addToCartAnalyticsData.netPrice,
    listPrice: addToCartAnalyticsData.listPrice,
    offerPricingId: addToCartAnalyticsData.offerPricingId,
    isEdoms: addToCartAnalyticsData.isEdoms,
    description: addToCartAnalyticsData.description,
    gradeLevels: addToCartAnalyticsData.gradeLevel,
    totalPrice: Math.round(addToCartAnalyticsData.quantity! * (addToCartAnalyticsData.netPrice || 0) * 100) / 100,
    quantity: addToCartAnalyticsData.quantity,
    store: addToCartAnalyticsData.store,
    showQuantity: addToCartAnalyticsData.showQuantity,
    showTotal: addToCartAnalyticsData.showTotal,
    showPrice: addToCartAnalyticsData.showPrice,
    externalUrl: addToCartAnalyticsData.externalUrl,
    instantCheckout: addToCartAnalyticsData.instantCheckout,
    showRemoveLink: addToCartAnalyticsData.showRemoveLink,
    user: getUserDataForUserAnalytics(addToCartAnalyticsData.state, true),
    author: addToCartAnalyticsData.contributors,
    edition: addToCartAnalyticsData.edition,
    ...((addToCartAnalyticsData.editionOrRelease || addToCartAnalyticsData.product?.editionOrRelease) && {
      editionOrRelease: addToCartAnalyticsData.editionOrRelease ?? addToCartAnalyticsData.product?.editionOrRelease,
    }),
    url: window.location.href,
    productType: resolvedProductType,
    mixedCart:
      isProfessional(getOrderBusinessCode(addToCartAnalyticsData.state) ?? '')
        ? isProfessionalHasMixedCart(addToCartAnalyticsData.state)
          ? 'y'
          : 'n'
        : undefined,
    isEvergreenPDP: !!addToCartAnalyticsData.product?.isEvergreenPDP,
    ...addToCartAnalyticsData.purchaseOption,
    ...getCartAnalyticsOverrides(addToCartAnalyticsData.state),
    ...getProductTypeBU({
      programIds: [addToCartAnalyticsData.programSourceId ?? ''],
    }),
    ...getSecondLevelBU({
      programId: addToCartAnalyticsData.programSourceId ?? '',
    }),
    ...getProductTypeInfo({
      programId: addToCartAnalyticsData.programSourceId ?? '',
    }),
    ...getPdpTypeInfo(
      addToCartAnalyticsData.showRemoveLink
        ? { offerType: addToCartAnalyticsData.offerType ?? '', pdpType: addToCartAnalyticsData.pdpType }
        : { pdpType: addToCartAnalyticsData.pdpType }
    ),
    ...getSearchTermObjectFromCartLocalStorage({
      programId: addToCartAnalyticsData.programSourceId ?? '',
      showRemoveLink: addToCartAnalyticsData.showRemoveLink ? true : false,
    }),
    ...getPdpLevelProductTitle({
      addToCartAnalyticsData,
    }),
    coverImage: addToCartAnalyticsData.coverImage,
    ...(addToCartAnalyticsData?.orderFlow && { orderFlow: addToCartAnalyticsData.orderFlow }),
    ...(isProfessionalProduct && titleType !== undefined && { titleType }),
    ...(isProfessionalProduct && division !== undefined && { division }),
    ...(titleType === PROFESSIONAL_FCD_TITLE_TYPE && {
      productType: PRODUCT_TYPE_FLASHCARD,
    }),
  };

  if (addToCartAnalyticsData.product?.programSourceId) {
    details.programSourceId = addToCartAnalyticsData.product.programSourceId;
  }

  if (
    !isProfessionalProduct &&
    titleType !== PROFESSIONAL_FCD_TITLE_TYPE &&
    addToCartAnalyticsData.product?.productType
  ) {
    details.productType = addToCartAnalyticsData.product.productType;
  }

  if (addToCartAnalyticsData.product?.release) {
    details.release = addToCartAnalyticsData.product.release;
  }

  if (addToCartAnalyticsData.hePDPViewType) {
    details.pdpViewType = addToCartAnalyticsData.hePDPViewType;
    details.pdpViewType = addToCartAnalyticsData.hePDPViewType || StudentInstructorToggleGroup.STUDENT;
  }

  if (addToCartAnalyticsData.dataset?.tabType) {
    details.tabType = addToCartAnalyticsData.dataset?.tabType;
  }

  if (addToCartAnalyticsData.durationType) {
    details.durationType = addToCartAnalyticsData.durationType;
  }

  if (isHeStorePurchaseFlow()) {
    Object.assign(details, {
      ...getHeStoreDataForAnalytics({
        isCreateEbook: addToCartAnalyticsData.isCreateProduct ?? false,
        formatDescription: addToCartAnalyticsData?.formatDesc,
        formatTypeDescription: addToCartAnalyticsData?.formatTypeDesc,
      }),
    })
  }

  Object.assign(
    details,
    getSharpenPromoAnalytics(
      [addToCartAnalyticsData.programSourceId ?? ''],
      getPromoCodeState(addToCartAnalyticsData.state)?.coupon
    )
  );

  return JSON.parse(JSON.stringify(details));
}

export function appendQueryParamToUrl(urlString: string, key: string, value: string) {
  try {
    let url;
    if (urlString.startsWith("http://") || urlString.startsWith("https://")) {
      url = new URL(urlString);
    } else {
      url = new URL(urlString, window.location.origin);
    }

    if (url.searchParams.has(key)) {
      return url.toString();
    }
    url.searchParams.set(key, value);

    return url.toString();
  } catch (error) {
    return urlString;
  }
}

export function checkBUParam() {
  if (!window.location.search) {
    return false;
  }
  const urlParams = new URLSearchParams(window.location.search);
  const buValue = urlParams.get("bu");
  return buValue === "articles" || buValue === "content";
}

export function getSearchPageAnalytics(
  searchTerm: string,
  totalSearchResults?: number,
) {
  const searchPageLSObject = getSearchPageLocalStorageObject();

  return searchPageLSObject
    ? {
      searchAttempts: searchPageLSObject.searchAttempts + 1,
      searchTerms: [
        ...searchPageLSObject.searchTerms,
        {
          searchTerm: searchTerm ? searchTerm : 'no query',
          totalSearchResults: totalSearchResults ?? undefined
        },
      ],
    } : {
      searchAttempts: 1,
      searchTerms: [{
        searchTerm: searchTerm ? searchTerm : 'no query',
        totalSearchResults: totalSearchResults ?? undefined
      }],
    }
}

export function getPdpPageAnalytics(
  programSourceId: string,
) {
  const pdpPageLSObject = getPdpPageLocalStorageObject();

  if (pdpPageLSObject) {
    return pdpPageLSObject[programSourceId] ?? undefined;
  } else {
    // use this part later while capturing analytics on page load
  }
}

export function getCartPageAnalytics(
  programSourceId: string,
) {
  const cartPageLSObject = getCartPageLocalStorageObject();

  if (cartPageLSObject) {
    return cartPageLSObject[programSourceId] ?? undefined;
  }
}

export function getSharpenTunnelFlowProperties({
  isConnectSharpenBundle,
  isSharpenCompanion,
  isSharpenAdvantage,
  itemType,
  flow,
  offerType,
  formatCode,
}: {
  isConnectSharpenBundle?: boolean;
  isSharpenCompanion?: boolean;
  isSharpenAdvantage?: boolean;
  itemType?: string;
  flow?: string;
  offerType?: string;
  formatCode?: string;
}): Record<string, any> {
  const analyticsProperties: {
    sharpen?: boolean;
    offerDescription?: string;
  } = {
    sharpen: true,
  };

  if (isSharpenCompanion) {
    const isMhGo = checkIsMhGoItemFromItemProperties({
      formatCode,
      offerType,
      itemType,
      flow,
    });
    const isAleksSharpenCompanion = isAleksSharpenCompanionBundle(offerType, isSharpenCompanion);
    analyticsProperties.offerDescription = isMhGo
      ? 'MHGO_WITH_SHARPEN_COMPANION'
      : isAleksSharpenCompanion
        ? 'ALEKS_WITH_SHARPEN_COMPANION'
        : 'CONNECT_WITH_SHARPEN_COMPANION';
  } else if (isSharpenAdvantage) {
    analyticsProperties.offerDescription = 'CONNECT_WITH_SHARPEN_ADVANTAGE';
  } else if (isConnectSharpenBundle) {
    analyticsProperties.offerDescription = 'CONNECT_WITH_SHARPEN';
  } else {
    delete analyticsProperties.sharpen;
  }

  return analyticsProperties;
}

export function getSharpenOrderType({
  isConnectSharpenBundle,
  isSharpenCompanion,
  isSharpenAdvantage,
  itemType,
  flow,
  offerType,
  formatCode,
  application,
}: {
  isConnectSharpenBundle?: boolean;
  isSharpenCompanion?: boolean;
  isSharpenAdvantage?: boolean;
  itemType?: string;
  flow?: string;
  offerType?: string;
  formatCode?: string;
  application?: string;
}) {
  const isMhGo = checkIsMhGoItemFromItemProperties({
    offerType,
    itemType,
    flow,
    formatCode,
  });
  if (!isMhGo && isAleksSharpenCompanionBundle(offerType, isSharpenCompanion)) {
    return OrderType.ALEKS_SHARPEN_COMPANION;
  } else if (!isMhGo && isSharpenCompanion) {
    return OrderType.CONNECT_BUNDLE_SHARPEN_COMPANION;
  } else if (!isMhGo && isSharpenAdvantage) {
    return OrderType.CONNECT_BUNDLE_SHARPEN_ADVANTAGE;
  } else if (!isMhGo && isConnectSharpenBundle) {
    return OrderType.CONNECT_BUNDLE_SHARPEN;
  }
  return `HE-${getOrderTypeTunnelFlow({
    application,
    itemType,
    isSharpenCompanion,
    offerType,
    flow
  }) || 'connect'
    }`;
}

export function getGuestCheckoutOrderType(application?: string) {
  const normalizedApplication = application?.trim();

  return normalizedApplication ? `HE-${normalizedApplication}` : OrderType.HE_NATIONAL;
}

export function removingSearchAnalyticsRelatedLocalStorageObjects() {
  mhecommLocalStorage.remove(SearchTermAnalyticsConstants.CART_PAGE_LOCAL_STORAGE_OBJECT);
  mhecommLocalStorage.remove(SearchTermAnalyticsConstants.PDP_PAGE_LOCAL_STORAGE_OBJECT);
  mhecommLocalStorage.remove(SearchTermAnalyticsConstants.SEARCH_PAGE_LOCAL_STORAGE_OBJECT);
  mhecommLocalStorage.remove(SearchTermAnalyticsConstants.RESET_SEARCH_LOCAL_STORAGE);
}

export function determineProgramSourceId(
  isbn: string,
  isbn13: string,
  businessUnit: string,
  programSourceId: string,
) {
  if (businessUnit === SEG) {
    return `S${isbn13 ?? isbn}`;
  }
  return programSourceId;
}

const businessUnitAnalyticsMapping: { [key: string]: string } = {
  K12: SEG_LOWER,
  SEG: SEG_LOWER,
  'SEG-STORE': SEG_LOWER,
  'SEG-PRODUCT': SEG_LOWER,
  'SEG-PROGRAM': SEG_LOWER,
  'PREK-12': SEG_LOWER,
  HIGHERED: HE_LOWER,
  HE: HE_LOWER,
  PROFESSIONAL: PROFESSIONAL,
};

export function determineBusinessUnit(
  businessUnit: string,
) {
  return businessUnitAnalyticsMapping[businessUnit]
    ? businessUnitAnalyticsMapping[businessUnit]
    : businessUnit.toLowerCase();
}

export function isVirtualProduct(poType) {
  return !PhysicalCardDescriptionList.includes(poType);
}

/**
 * Derive analytics orderType and optional productTypeBu for order history events.
 * Uses tunnel item metadata when present, otherwise falls back to order segment/business unit.
 * @param order Order history record to derive analytics fields from.
 */
export function getOrderTypeFromOrderDetails(order: OrderHistory) {
  const orderDetails = order.userOrderDetails ?? [];
  if (!orderDetails.length) {
    return '';
  }

  let orderType = '';
  let productTypeBu = '';

  const tunnelDetail = orderDetails.find((detail) => detail.application || detail.flow || detail.itemType);
  if (tunnelDetail) {
    orderType = getSharpenOrderType({
      offerType: tunnelDetail.offerType,
      itemType: tunnelDetail.itemType,
      flow: tunnelDetail.flow,
      application: tunnelDetail.application,
      formatCode: tunnelDetail.formatCode,
      isConnectSharpenBundle: tunnelDetail.isConnectSharpenBundle,
      isSharpenCompanion: tunnelDetail.isSharpenCompanion,
      isSharpenAdvantage: tunnelDetail.isSharpenAdvantage,
    });
  } else if (
    order.orderSegment?.toLowerCase() === PROFESSIONAL.toLowerCase() ||
    orderDetails.some((detail) => detail.businessUnit?.toLowerCase() === PROFESSIONAL.toLowerCase())
  ) {
    orderType = OrderType.PROFESSIONAL;
    const { productTypeBu: productTypes } = getProductTypeBU({
      programIds: order.userOrderDetails
        .filter((orderDetails) => !!orderDetails.programSourceId)
        .map((orderDetails) => orderDetails.programSourceId!),
    });
    if (productTypes) {
      productTypeBu = productTypes;
    }
  } else {
    const orderSegment = order.orderSegment?.toLowerCase();
    if (orderSegment?.includes('custom') || isHePrivateStoreOrder(order)) {
      orderType = OrderType.HE_CUSTOM_STORE;
    } else if (orderSegment?.includes('ebook') || isHeCustomStoreOrder(order)) {
      orderType = OrderType.HE_CREATE;
    } else if (orderSegment === HE.toLowerCase()) {
      orderType = OrderType.HE_NATIONAL;
    } else if (orderSegment === SEG.toLowerCase()) {
      orderType = OrderType.SEG_NATIONAL;
    }
  }

  return {
    ...(orderType && { orderType }),
    ...(productTypeBu && { productTypeBu }),
    ...getSharpenPromoAnalytics(getOrderProgramIds(order), order.couponCode),
  };
}

export function getSharpenOrderFlowAnalyticsData() {
  return {
    orderFlow: AnalyticsOrderFlowTypes.HE_CONNECT_SHARPEN,
  }
}

/**
 * Returns order flow analytics data for Sharpen tunnel purchases when applicable.
 * @param params Input containing the program source identifier and tunnel-flow flag.
 */
export function getOrderFlowAnalyticsData(
  { programSourceId, isTunnelPurchaseFlow = false }: { programSourceId?: string; isTunnelPurchaseFlow?: boolean },
) {
  if (isSharpenPlus(programSourceId) && isTunnelPurchaseFlow) {
    return getSharpenOrderFlowAnalyticsData();
  }
  return {};
}
